import {
  PreviewRow,
  PreviewTable,
  PreviewWorksheet,
} from "@features/upload/FileContentPreviewTable"
import {
  getFileFormat,
  getTableRowsFromCsvContent,
  readFile,
  removeEmptyRowsFromCsvFileContent,
} from "utils/file"
import * as XLSX from "sheetjs-style-v2"

export const getWorksheetsFromFile = async (
  file: File,
  maxRows?: number,
): Promise<PreviewWorksheet[]> => {
  switch (getFileFormat(file)) {
    case ".csv":
      // CSV does not support multiple worksheets, so we return a single worksheet with tables parsed from the CSV content
      return [
        {
          name: "File content",
          tables: await getTablesFromCsvFile(file, maxRows),
        },
      ]
    case ".xlsx":
      return getWorksheetsFromXlsxFile(file, maxRows)
    default:
      return []
  }
}

export const getWorksheetsFromXlsxFile = async (
  file: File,
  maxRows?: number,
): Promise<PreviewWorksheet[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = e.target?.result
        const workbook = XLSX.read(data, { type: "array" })

        const worksheets: PreviewWorksheet[] = workbook.SheetNames.map((sheetName) => {
          const worksheet = workbook.Sheets[sheetName]
          if (!worksheet) return { name: sheetName, tables: [] }

          // Convert sheet to array of arrays (with empty rows included)
          let allRows = XLSX.utils.sheet_to_json<string[]>(worksheet, {
            header: 1,
            defval: "",
            blankrows: true, // Needed to detect table separators (empty rows)
          })

          // Apply maxRows limit if specified
          if (typeof maxRows === "number") {
            allRows = allRows.slice(0, maxRows + 1) // +1 to account for header row
          }

          // Split into multiple tables based on empty rows
          const tables: PreviewTable[] = []
          let currentTableRows: string[][] = []

          for (const row of allRows) {
            const isEmptyRow = row.every((cell) => ["", null, undefined].includes(cell))

            if (isEmptyRow) {
              // Empty row detected - possible end of current table
              if (currentTableRows.length > 0) {
                const table = convertRowsToTable(currentTableRows)
                if (table.rows.length > 0) tables.push(table)

                currentTableRows = []
              }
            } else {
              // Non-empty row - add to current table
              currentTableRows.push(row)
            }
          }

          // Last table if there's no trailing empty row
          if (currentTableRows.length > 0) {
            const table = convertRowsToTable(currentTableRows)
            if (table.rows.length > 0) tables.push(table)
          }

          return { name: sheetName, tables }
        })

        resolve(worksheets)
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error("Failed to read file"))
    reader.readAsArrayBuffer(file)
  })
}

const convertRowsToTable = (rows: string[][]): PreviewTable => {
  if (rows.length === 0) return { rows: [] }

  const headers = rows[0] ?? []
  const dataRows = rows.slice(1)

  return {
    rows: dataRows.map((row) => {
      const rowObject: PreviewRow = {}

      headers.forEach((header, index) => {
        const headerKey = header || `Column ${index + 1}`
        rowObject[headerKey] = String(row[index] ?? "")
      })

      return rowObject
    }),
  }
}

export const getTablesFromCsvFile = async (
  file: File,
  maxRows?: number,
): Promise<PreviewTable[]> => {
  let fileContent = await readFile(file)
  fileContent = removeEmptyRowsFromCsvFileContent(fileContent)

  // on different systems, end of line character may differ
  let tablesContent = fileContent.split("\r\n\r\n") // windows - CRLF
  if (tablesContent.length < 2) tablesContent = fileContent.split("\n\n") // linux - LF
  if (tablesContent.length < 2) tablesContent = fileContent.split("\r\r") // macOS - CR

  return tablesContent.map((tableContent) => ({
    rows: getTableRowsFromCsvContent(tableContent, maxRows),
  }))
}
